import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import { errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import type { Player } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { useCoachUsers, type RosterMutations } from "../hooks/useRoster";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Link-account modal — Coach > Roster.
 *
 * Ports the legacy `openCoachLinkModal` (`js/coaching/roster.js`).
 * Links a team user account to a roster player so the player's
 * feedback reaches that account (a guardian, the player's own
 * account, or another related account).
 *
 * `useCoachUsers` (gated open here) loads the team's linkable
 * users; the picker excludes accounts already linked to THIS
 * player so a coach can't double-link the same user. The
 * relationship enum mirrors `PlayerUserLink.relationship` —
 * `guardian` / `self` / `other`; the backend's
 * `CreatePlayerUserLinkRequest` validates against
 * `_VALID_PLAYER_RELATIONSHIPS`.
 */

const RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: "guardian" | "self" | "other";
  label: string;
}> = [
  { value: "guardian", label: "Parent / guardian" },
  { value: "self", label: "Player" },
  { value: "other", label: "Other" },
];

function relationshipLabel(value: "guardian" | "self" | "other") {
  return RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ?? "Other";
}

export type LinkAccountModalProps = {
  player: Player;
  mutations: RosterMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function LinkAccountModal({
  player,
  mutations,
  onClose,
  onSaved,
}: LinkAccountModalProps) {
  const toast = useToast();
  // Fetch the team's linkable users only while this modal is open.
  const usersQuery = useCoachUsers(true);
  const [userId, setUserId] = useState("");
  const [relationship, setRelationship] = useState<
    "guardian" | "self" | "other"
  >("guardian");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [removedLinkIds, setRemovedLinkIds] = useState<Set<number>>(
    () => new Set(),
  );

  const visibleLinks = useMemo(
    () => (player.links ?? []).filter((link) => !removedLinkIds.has(link.id)),
    [player.links, removedLinkIds],
  );

  // Exclude accounts already linked to this player.
  const linkedUserIds = useMemo(
    () => new Set(visibleLinks.map((link) => link.user_id)),
    [visibleLinks],
  );
  const availableUsers = useMemo(
    () =>
      (usersQuery.data?.users ?? []).filter((u) => !linkedUserIds.has(u.id)),
    [usersQuery.data, linkedUserIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) {
      setError("Pick an account to link.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await mutations.linkAccount({
        player_id: player.id,
        user_id: userId,
        relationship,
      });
      toast.success("Account linked");
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : "Could not link the account.",
      );
      toast.error("Couldn't link account", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (linkId: number) => {
    setError(null);
    setRemovingId(linkId);
    try {
      await mutations.unlinkAccount(linkId);
      setRemovedLinkIds((current) => new Set(current).add(linkId));
      toast.success("Account link removed");
      onSaved?.();
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Couldn't remove account link", {
        description: errorMessage(err),
      });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <CoachModal
      title={`Manage linked accounts · ${player.display_name}`}
      onClose={onClose}
      data-testid="roster-link-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="roster-link-account-form"
            disabled={submitting || availableUsers.length === 0}
            data-testid="link-modal-submit"
          >
            {submitting ? "Linking…" : "Link account"}
          </Button>
        </div>
      }
    >
      <form
        id="roster-link-account-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
      >
        <section aria-labelledby="linked-accounts-heading">
          <h3
            id="linked-accounts-heading"
            className="font-display text-base text-ink"
          >
            Linked accounts
          </h3>
          {visibleLinks.length > 0 ? (
            <ul className="mt-2 divide-y divide-rule rounded border border-rule bg-elevated">
              {visibleLinks.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">
                      {link.display_name}
                    </p>
                    <p className="truncate text-xs text-ink-muted">
                      @{link.username}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-field px-2 py-1 text-xs text-ink-muted">
                      {relationshipLabel(link.relationship)}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleRemove(link.id)}
                      disabled={removingId !== null}
                      className="rounded px-2 py-1 text-sm text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                    >
                      {removingId === link.id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 rounded border border-rule bg-elevated px-3 py-3 text-sm text-ink-muted">
              No accounts are linked yet.
            </p>
          )}
        </section>

        <div className="border-t border-rule pt-4">
          <h3 className="font-display text-base text-ink">
            Add another account
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Choose from verified accounts with access to this club.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Account
          </span>
          <TkticSelect
            value={userId}
            onChange={(next) => setUserId(next)}
            data-testid="link-modal-user"
            disabled={usersQuery.isLoading}
            ariaLabel="Account"
            options={[
              {
                value: "",
                label: usersQuery.isLoading
                  ? "Loading accounts…"
                  : availableUsers.length === 0
                    ? "No linkable accounts"
                    : "Select an account…",
              },
              ...availableUsers.map((u) => ({
                value: u.id,
                label: `${u.display_name} · @${u.username}`,
              })),
            ]}
          />
        </label>

        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Relationship
          </span>
          <TkticSelect
            value={relationship}
            onChange={(next) =>
              setRelationship(next as "guardian" | "self" | "other")
            }
            data-testid="link-modal-relationship"
            ariaLabel="Relationship"
            options={RELATIONSHIP_OPTIONS.map((r) => ({
              value: r.value,
              label: r.label,
            }))}
          />
        </label>
        </div>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="link-modal-error"
          >
            {error}
          </p>
        ) : null}
      </form>
    </CoachModal>
  );
}
